// The member disclaimer and the list of public sources behind the lessons.
// The list comes from research/facts.json. scripts/build.mjs strips that file
// down to its { title, url } source entries when it bundles the app, so the
// member bundle carries no fact text, ids or lesson mapping.

import facts from '../../research/facts.json';
import { esc } from './ui';

// Disclaimer wording: pending attorney review (2026-09-28, rewritten in a wellness voice to match the Terms of Use)
export const DISCLAIMER: readonly string[] = [
  'Whole Health Harmony is a holistic wellness program. It exists to promote wellness and to share everyday habits that support your wellbeing. It is wellness education, not medical care, and it is not intended to diagnose, treat, cure or prevent any disease.',
  'Marie is an automated wellness guide, not a person or a healthcare provider.',
  'You are the expert on your own body. It is always wise to talk with your healthcare provider before you change your routine, your supplements or your medicines, especially if you are pregnant or have a health condition.',
  'In an emergency, call 911. In a crisis, call or text 988.',
  'Our wellness education is built on public health information from U.S. government sources, such as the CDC, NIH, FDA and USDA, and from other trusted health sources. None of these sources endorse Whole Health Harmony.',
];
export const SOURCES_LINK_TEXT = 'See our sources';

export interface Source { title: string; url: string }
export interface SourceGroup { key: string; label: string; note: string; items: Source[] }
interface FactsFile { facts: { sources?: { title?: string; url?: string }[] }[] }

/** Display order. Hosts are matched without a leading "www.". */
const GROUPS: { key: string; label: string; note: string; hosts: string[] }[] = [
  { key: 'cdc', label: 'Centers for Disease Control and Prevention (CDC)', note: '', hosts: ['cdc.gov'] },
  { key: 'nih', label: 'National Institutes of Health (NIH)', note: 'Includes MedlinePlus and the NIH institutes and offices, such as NHLBI, NCCIH, NIA, NIDDK and the Office of Dietary Supplements.', hosts: ['nih.gov', 'medlineplus.gov'] },
  { key: 'fda', label: 'U.S. Food and Drug Administration (FDA)', note: '', hosts: ['fda.gov'] },
  { key: 'usda', label: 'U.S. Department of Agriculture (USDA) and FoodSafety.gov', note: '', hosts: ['usda.gov', 'foodsafety.gov', 'myplate.gov', 'dietaryguidelines.gov'] },
  { key: 'owh', label: 'Office on Women’s Health', note: '', hosts: ['womenshealth.gov'] },
  { key: 'uspstf', label: 'U.S. Preventive Services Task Force (USPSTF)', note: '', hosts: ['uspreventiveservicestaskforce.org'] },
  { key: 'gov', label: 'Other U.S. government sources', note: '', hosts: [] },
  { key: 'other', label: 'Other sources', note: 'These are not U.S. government websites.', hosts: [] },
];

const bareHost = (url: string) => { try { return new URL(url).hostname.toLowerCase().replace(/^www\./, ''); } catch { return ''; } };
const hostIs = (h: string, d: string) => h === d || h.endsWith(`.${d}`);

/** Which group a source URL belongs to, from its host. */
export function agencyOf(url: string): string {
  const h = bareHost(url);
  const g = GROUPS.find((x) => x.hosts.some((d) => hostIs(h, d)));
  if (g) return g.key;
  return /\.(gov|mil)$/.test(h) ? 'gov' : 'other';
}

/** Same page, even if written with or without "www." or a trailing slash. */
function urlKey(url: string) {
  try { const u = new URL(url); return `${bareHost(url)}${u.pathname.replace(/\/+$/, '')}${u.search}`; } catch { return url.trim(); }
}

/** Every unique http(s) source, grouped by agency, titles sorted. Empty groups are left out. */
export function groupSources(file: FactsFile): SourceGroup[] {
  const seen = new Map<string, Source>();
  for (const f of file.facts ?? []) {
    for (const s of f.sources ?? []) {
      const url = (s?.url ?? '').trim(); const title = (s?.title ?? '').trim();
      if (!/^https?:\/\//i.test(url)) continue;
      const k = urlKey(url);
      if (!seen.has(k)) seen.set(k, { title: title || bareHost(url), url });
    }
  }
  const byKey = new Map<string, Source[]>();
  for (const s of seen.values()) { const k = agencyOf(s.url); byKey.set(k, [...(byKey.get(k) ?? []), s]); }
  return GROUPS.filter((g) => byKey.has(g.key)).map((g) => ({
    key: g.key, label: g.label, note: g.note,
    items: byKey.get(g.key)!.sort((a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }) || a.url.localeCompare(b.url)),
  }));
}

export const SOURCE_GROUPS: SourceGroup[] = groupSources(facts as FactsFile);

/** The disclaimer paragraphs plus the "See our sources" link (omit the link on the Sources page itself). */
export function disclaimerHtml(link: string | null = 'href="#/sources"') {
  return `<div class="disclaimer">${DISCLAIMER.map((p) => `<p>${esc(p)}</p>`).join('')}${link ? `<a ${link}>${SOURCES_LINK_TEXT}</a>` : ''}</div>`;
}

/** The grouped source links. */
export function sourcesHtml(groups: SourceGroup[] = SOURCE_GROUPS) {
  if (!groups.length) return '<p class="muted" style="margin:0">Our source list is being updated. Please check back soon.</p>';
  return groups.map((g) => `<section class="card col src-group" aria-labelledby="src-${g.key}" style="gap:8px">
      <h2 id="src-${g.key}" class="serif" style="font-size:18px;line-height:1.25">${esc(g.label)}</h2>
      ${g.note ? `<span class="muted" style="font-size:14px;line-height:1.5">${esc(g.note)}</span>` : ''}
      <ul class="src-list">${g.items.map((s) => `<li><a href="${esc(s.url)}" target="_blank" rel="noopener noreferrer">${esc(s.title)}</a></li>`).join('')}</ul>
    </section>`).join('');
}

/** Supplement brand the practice uses. Linked from Profile only, never from lessons (owner, 2026-09-27). */
export const SUPPLEMENT_LIBRARY_URL = 'https://www.designsforhealth.com/products/';
